import React, { useState, useRef, useEffect, useCallback } from "react"; // eslint-disable-line no-unused-vars
import { useLoadScript } from "@react-google-maps/api";
import { LocationOn } from "@material-ui/icons";
import styles from "../styles/InputEndereco.module.scss";
import { Constants } from "../utils/Constants";

const GOOGLE_MAPS_LIBRARIES = ["places"];

let sessionToken = null;

export default function InputEndereco({
  name = "",
  value = "",
  label = "",
  required = false,
  disabled = false,
  errors = {},
  touched = {},
  onChange = () => {},
  onSelect = () => {},
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: Constants.mapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [is_focused, set_is_focused] = useState(false);
  const [sugestoes, set_sugestoes] = useState([]);
  const [loading, set_loading] = useState(false);
  const [mostrar_dropdown, set_mostrar_dropdown] = useState(false);
  const container_ref = useRef(null);
  const debounce_ref = useRef(null);

  const is_floating = is_focused || Boolean(value);
  const has_error = (touched?.[name]) && errors?.[name];

  useEffect(() => {
    const handle_click_fora = (evento) => {
      if (container_ref.current && !container_ref.current.contains(evento.target)) {
        set_mostrar_dropdown(false);
      }
    };

    document.addEventListener("mousedown", handle_click_fora);

    return () => {
      document.removeEventListener("mousedown", handle_click_fora);

      if (debounce_ref.current) {
        clearTimeout(debounce_ref.current);
      }
    };
  }, []);

  const buscar = useCallback(async (texto) => {
    if (!texto || texto.trim().length < 3) {
      set_sugestoes([]);
      set_mostrar_dropdown(false);

      return;
    }

    if (!sessionToken) {
      sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    }

    set_loading(true);

    try {
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: texto.trim(),
        sessionToken,
        includedRegionCodes: ["br"],
        language: "pt-BR",
      });

      const lista = suggestions
        .filter((item) => item.placePrediction)
        .map((item) => ({
          place_id: item.placePrediction.placeId,
          description: item.placePrediction.text.text,
          placePrediction: item.placePrediction,
        }));

      set_sugestoes(lista);
      set_mostrar_dropdown(lista.length > 0);
    } catch {
      set_sugestoes([]);
      set_mostrar_dropdown(false);
    } finally {
      set_loading(false);
    }
  }, []);

  const handle_change = (evento) => {
    const texto = evento.target.value;

    onChange(evento);

    if (!isLoaded) {
      return;
    }

    if (debounce_ref.current) {
      clearTimeout(debounce_ref.current);
    }

    debounce_ref.current = setTimeout(() => {
      buscar(texto);
    }, 300);
  };

  const handle_selecionar = async (sugestao) => {
    set_mostrar_dropdown(false);
    set_sugestoes([]);
    sessionToken = null;

    try {
      const lugar = sugestao.placePrediction.toPlace();

      await lugar.fetchFields({
        fields: ["formattedAddress", "location", "addressComponents"],
      });

      let cidade = null;
      let cidade_fallback = null;
      let uf = null;

      if (lugar.addressComponents) {
        for (const componente of lugar.addressComponents) {
          if (componente.types.includes("locality")) {
            cidade = componente.longText;
          }

          if (componente.types.includes("administrative_area_level_2")) {
            cidade_fallback = componente.longText;
          }

          if (componente.types.includes("administrative_area_level_1")) {
            uf = componente.shortText;
          }
        }
      }

      cidade = cidade || cidade_fallback;

      if (cidade) {
        cidade = cidade.replace(/\s*\([^)]*\)\s*$/, "").replace(/\s*-\s*[A-Z]{2}\s*$/, "").trim();
      }

      onSelect({
        endereco: lugar.formattedAddress || sugestao.description,
        latitude: lugar.location?.lat() ?? null,
        longitude: lugar.location?.lng() ?? null,
        cidade,
        uf,
        place_id: sugestao.place_id,
      });
    } catch {
      onSelect({
        endereco: sugestao.description,
        latitude: null,
        longitude: null,
        cidade: null,
        uf: null,
        place_id: sugestao.place_id,
      });
    }
  };

  return (
    <div className={styles.container} ref={container_ref}>
      <div className={styles.inputWrapper}>
        {label && (
          <label
            className={`${styles.floatingLabel} ${is_floating ? styles.floating : ""} ${has_error ? styles.labelError : ""}`}
          >
            {label} {required && <span className={styles.requiredStar}>*</span>}
          </label>
        )}

        <input
          name={name}
          type="text"
          value={value}
          onChange={handle_change}
          onFocus={() => set_is_focused(true)}
          onBlur={() => set_is_focused(false)}
          className={`${styles.input} ${is_floating ? styles.focused : ""} ${has_error ? styles.hasError : ""}`}
          disabled={disabled || !isLoaded}
          autoComplete="off"
        />
      </div>

      {mostrar_dropdown && (
        <div className={styles.dropdown}>
          {loading && (
            <div className={styles.loading}>Buscando endereços...</div>
          )}

          {sugestoes.map((sugestao) => (
            <div
              key={sugestao.place_id}
              className={styles.dropdownItem}
              onMouseDown={() => handle_selecionar(sugestao)}
            >
              <LocationOn className={styles.dropdownIcon} />
              <p className={styles.dropdownTexto}>{sugestao.description}</p>
            </div>
          ))}
        </div>
      )}

      {has_error && (
        <p className={styles.errorMessage}>{errors?.[name]}</p>
      )}
    </div>
  );
}
